import React, { useEffect } from "react";
import { map, switchMap } from "rxjs";
import xstream from "state-x-stream";
import PropTypes from "prop-types";

function controller({ props$ }) {
  function extractSampleInfo(state) {
    const { qcReport, sampleId } = state;
    const currentSample = qcReport.sampleIdMap[sampleId];
    const refSet = currentSample?.refSampleIdSet || new Set();

    if (!currentSample) return [];

    const samples = qcReport.samples.map((x) => ({
      ...x,
      isCurrent: x.id === sampleId,
      isRef: refSet.has(x.id),
    }));

    const currentSamples = samples.filter((x) => x.isCurrent);
    const refSamples = samples.filter((x) => x.isRef);
    const otherSamples = samples.filter((x) => !x.isCurrent && !x.isRef);

    return [...currentSamples, ...refSamples, ...otherSamples];
  }

  function setSamplePreview(previewId) {
    const state = props$.value?.stage?.state$.value || {};
    if (props$.value.zoomService.processes$.value) return;
    if (previewId === state.previewId) return;

    const { qcReport, graphSampleDetails } = state;
    const sampleName = qcReport?.sampleIdMap[previewId]?.name;
    const previewCsv = graphSampleDetails?.[sampleName];

    props$.value.stage.setState({ ...state, previewId, previewCsv });
  }

  return {
    setSamplePreview,
    samples: props$.pipe(
      switchMap((x) => x.stage.state$),
      map((x) => extractSampleInfo(x))
    ),
  };
}

function InfoRenderer({ samples, setSamplePreview }) {
  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
    return () => window.dispatchEvent(new Event("resize"));
  });

  return samples && samples.length ? (
    <div className="tw-w-72">
      <div className="tw-border-b border-1 color-1">
        <div className="tw-h-10 tw-px-4 tw-pt-3 tw-font-semibold">Samples</div>
      </div>

      <div className="tw-p-4 tw-italic tw-text-2xs tw-text-center tw-opacity-70 bg-2 tw-rounded-lg tw-m-4 color-1">
        You can press and <b>HOLD</b> the eye icon <i className="fa fa-eye" />
        <br /> to preview other samples
      </div>

      <div className="tw-text-3xs color-1 tw-p-2">
        <table className="tw-w-full">
          <tbody>
            {samples.map((x) => {
              return (
                <tr key={x.id} className="tw-border-b border-1 color-3">
                  <td className="tw-p-2">
                    <span className={x.isCurrent ? "tw-font-bold" : ""}>
                      {x.name}
                    </span>
                    <div
                      title="Reference Sample"
                      className="tw-text-sky-500 tw-inline-block tw-pl-2"
                    >
                      {x.isRef ? "[REF]" : ""}
                    </div>
                    <div className="tw-inline-block tw-pl-2">
                      {x.isRestricted ? (
                        <i
                          data-tooltip="The CNV results for this sample are restricted to a specific Gene List. <br/>Displaying this sample in the browser might reveal results that go beyond the selected genes."
                          data-tooltip-class="genome-browser-small-tooltip"
                          data-tooltip-dangerous-html="true"
                          className="fa fa-exclamation-circle tw-text-red-500"
                        />
                      ) : null}
                    </div>
                  </td>
                  {x.isCurrent ? (
                    <td
                      data-tooltip="Current Sample"
                      data-tooltip-dangerous-html="true"
                      data-tooltip-class="genome-browser-small-tooltip"
                      className="tw-p-2 tw-w-9 tw-opacity-50"
                    >
                      <i className="fa fa-eye-slash tw-text-sm" />
                    </td>
                  ) : (
                    <td
                      data-tooltip="Click and <b>HOLD</b> to preview sample"
                      data-tooltip-dangerous-html="true"
                      data-tooltip-class="genome-browser-small-tooltip"
                      className="tw-p-2 tw-w-9 tw-opacity-70 hover:tw-opacity-100"
                    >
                      <div
                        tabIndex={-1}
                        className="tw-outline-none tw-border-none tw-cursor-pointer tw-z-10"
                        onMouseDown={() => setSamplePreview(x.id)}
                        onMouseLeave={() => setSamplePreview(null)}
                        onMouseUp={() => setSamplePreview(null)}
                      >
                        <i className="fa fa-eye tw-text-sm hover:tw-text-sky-500 tw-cursor-pointer tw-z-0" />
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  ) : (
    <div className="tw-p-4 color-3 tw-w-64 tw-my-6">
      Sample info not available
    </div>
  );
}

InfoRenderer.propTypes = {
  setSamplePreview: PropTypes.func.isRequired,
  samples: PropTypes.arrayOf(
    PropTypes.shape({
      sample: PropTypes.string,
      isCurrent: PropTypes.bool,
      isRef: PropTypes.bool,
    })
  ).isRequired,
};

export default xstream(controller)(InfoRenderer);
